/**
 * What a viewer can do TO a message, for any thread shape.
 *
 * The conversation view and the thread view render different layouts over the same
 * message rows, and both need the identical set: which bubble is open in the inline
 * editor, which one is waiting on the delete confirmation, which one is waiting on the
 * unpin confirmation, and the pin toggle with its toasts — plus the quote the open editor
 * holds, since a comment's edit may change it. Holding that here is what keeps
 * the two views layout-only — and keeps the pin toast wording from drifting between them.
 *
 * Pinning is a direct one-click action; unpinning is confirmation-gated (like delete),
 * because a pin can sit far back in the thread and a stray click should not silently
 * remove it. So `togglePin` pins immediately but routes an unpin through `messagePendingUnpin`.
 */

import { toast } from "@birdwatch/ui/toast";
import { useCallback, useState } from "react";

import type {
  ChannelMessage,
  ChannelQuotedMessage,
} from "../_api/channel-endpoints";
import { useChannelMessageMutations } from "../_api/use-channel-message-mutations";
import { useChannelPins } from "../_api/use-channel-pins";
import { canEditQuote, toQuotedMessage } from "../_libs/quoted-message";

export function useChannelMessageInteractions(cid: string) {
  const { deleteMessage, isDeleting } = useChannelMessageMutations(cid);
  const { pinMessage, unpinMessage, isPinning } = useChannelPins(cid);

  const [editing, setEditing] = useState<{
    messageId: string;
    /** What the editor's Save writes — seeded from the message, changed by Reply or ✕. */
    quote: ChannelQuotedMessage | null;
  } | null>(null);
  const [messagePendingDelete, setMessagePendingDelete] =
    useState<ChannelMessage | null>(null);
  const [messagePendingUnpin, setMessagePendingUnpin] =
    useState<ChannelMessage | null>(null);

  const startEdit = useCallback(
    (message: ChannelMessage) =>
      setEditing({
        messageId: message.id,
        quote: message.quotedMessage ?? null,
      }),
    [],
  );
  const cancelEdit = useCallback(() => setEditing(null), []);
  const changeEditingQuote = useCallback(
    (quote: ChannelQuotedMessage | null) =>
      setEditing((current) => (current ? { ...current, quote } : null)),
    [],
  );

  /**
   * "Quote reply" while a comment's editor is open re-quotes the comment being edited rather
   * than starting a new reply — the editor is where the viewer is writing. Returns whether
   * it took the action; the caller falls back to its composer's reply.
   */
  const quoteIntoEdit = useCallback(
    (message: ChannelMessage) => {
      if (!editing || !canEditQuote(cid)) return false;
      // A comment cannot quote itself (the platform 400s it); Reply on it is a no-op.
      if (message.id !== editing.messageId) {
        changeEditingQuote(toQuotedMessage(message));
      }
      return true;
    },
    [editing, cid, changeEditingQuote],
  );

  const togglePin = useCallback(
    async (message: ChannelMessage) => {
      if (message.pinnedAt) {
        setMessagePendingUnpin(message);
        return;
      }
      try {
        await pinMessage(message.id);
        toast.success({ title: "Message pinned!" });
      } catch (error) {
        toast.error({ title: "Can't update the pin!" });
        console.error(error);
      }
    },
    [pinMessage],
  );

  return {
    editingMessageId: editing?.messageId ?? null,
    editingQuote: editing?.quote ?? null,
    startEdit,
    cancelEdit,
    changeEditingQuote,
    quoteIntoEdit,
    messagePendingDelete,
    requestDelete: setMessagePendingDelete,
    dismissDelete: useCallback(() => setMessagePendingDelete(null), []),
    deleteMessage,
    isDeleting,
    togglePin,
    messagePendingUnpin,
    requestUnpin: setMessagePendingUnpin,
    dismissUnpin: useCallback(() => setMessagePendingUnpin(null), []),
    unpinMessage,
    isUnpinning: isPinning,
  };
}
